import { apiClient } from './client';

/**
 * Phase 12.1 — Workspaces frontend client.
 *
 * Mirrors backend routes at /api/workspaces. Sensitive ops (create / update /
 * archive / member CRUD) accept an optional 6-digit TOTP code that is sent
 * as the `X-TOTP-Code` header. Backend will respond with one of:
 *   - 412 TOTP_REQUIRED       — caller has not enrolled 2FA yet
 *   - 401 TOTP_CODE_REQUIRED  — header missing
 *   - 401 TOTP_INVALID        — code wrong/expired
 * The UI uses these codes to decide between «open enrollment» / «open
 * code modal» / «show invalid». See TotpModal.tsx + workspaceStore.ts.
 */

export interface Workspace {
  id: string;
  slug: string;
  displayName: string;
  ownerId: string;
  brandColor: string | null;
  iconUrl: string | null;
  archived: boolean;
  archivedReason?: string | null;
  createdAt: string;
  // Phase 12.1 — может отсутствовать на старых серверах.
  certIssuedAt?: string | null;
  lastProvisionStatus?: 'success' | 'failed' | null;
}

export interface ProvisionResult {
  ok: boolean;
  exitCode: number;
  log: string;
  provisionedAt: string | null;
}

export interface WorkspaceMember {
  id: string;
  workspaceId: string;
  userId: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  joinedAt: string;
  user?: { id: string; username: string; displayName: string; avatarUrl: string | null };
}

export interface CreateWorkspaceInput {
  ownerUserId: string;
  slug: string;
  displayName: string;
  brandColor?: string | null;
  iconUrl?: string | null;
}

export interface UpdateWorkspaceInput {
  displayName?: string;
  brandColor?: string | null;
  iconUrl?: string | null;
}

function totpHeader(totpCode?: string) {
  return totpCode ? { headers: { 'X-TOTP-Code': totpCode } } : {};
}

export const workspacesApi = {
  list: () => apiClient.get<Workspace[]>('/api/workspaces').then((r) => r.data),

  get: (slug: string) =>
    apiClient.get<Workspace>(`/api/workspaces/${slug}`).then((r) => r.data),

  create: (payload: CreateWorkspaceInput, totpCode?: string) =>
    apiClient.post<Workspace>('/api/workspaces', payload, totpHeader(totpCode)).then((r) => r.data),

  update: (slug: string, payload: UpdateWorkspaceInput, totpCode?: string) =>
    apiClient.patch<Workspace>(`/api/workspaces/${slug}`, payload, totpHeader(totpCode)).then((r) => r.data),

  archive: (slug: string, reason: string, totpCode?: string) =>
    apiClient.delete<Workspace>(`/api/workspaces/${slug}`, {
      ...totpHeader(totpCode),
      data: { reason },
    }).then((r) => r.data),

  getMembers: (slug: string) =>
    apiClient.get<WorkspaceMember[]>(`/api/workspaces/${slug}/members`).then((r) => r.data),

  addMember: (slug: string, userId: string, role: 'ADMIN' | 'MEMBER', totpCode?: string) =>
    apiClient.post<WorkspaceMember>(
      `/api/workspaces/${slug}/members`,
      { userId, role },
      totpHeader(totpCode),
    ).then((r) => r.data),

  removeMember: (slug: string, userId: string, totpCode?: string) =>
    apiClient.delete<void>(`/api/workspaces/${slug}/members/${userId}`, totpHeader(totpCode))
      .then(() => undefined),

  // Phase 12.1 — запускает host-скрипт (certbot + nginx). ~15-30s на живом прогоне,
  // ~1s на dryRun. TOTP обязателен.
  provision: (slug: string, totpCode?: string, dryRun = false) =>
    apiClient.post<ProvisionResult>(
      `/api/workspaces/${slug}/provision`,
      { dryRun },
      { ...totpHeader(totpCode), timeout: 180_000 },
    ).then((r) => r.data),
};
